/** Share links live in the URL fragment, which browsers never send to servers. */

export type HashRoute =
  { kind: 'share'; payload: string } | { kind: 'imgur'; id: string } | { kind: 'none' };

/** v2 view links: `#<imgurId>`. */
const IMGUR_HASH = /^#([a-zA-Z0-9]{3,10})$/;
/**
 * Share links are `<site>#<payload>`, with no prefix: every character counts on
 * social media. Payloads are always longer than the ≤ 10-character Imgur ids
 * of v2 view links, so the two never collide.
 */
const PAYLOAD = /^[A-Za-z0-9_-]{11,}$/;

/** Links longer than this get a warning: some chat apps truncate them. */
export const LONG_LINK_THRESHOLD = 8000;

export function parseHash(hash: string): HashRoute {
  const imgur = IMGUR_HASH.exec(hash)?.[1];
  if (imgur !== undefined) return { kind: 'imgur', id: imgur };
  const payload = hash.slice(1);
  return hash.startsWith('#') && PAYLOAD.test(payload)
    ? { kind: 'share', payload }
    : { kind: 'none' };
}

export function buildShareUrl(
  location: { origin: string; pathname: string },
  payload: string,
): string {
  return `${location.origin}${location.pathname}#${payload}`;
}

/**
 * Pulls a share payload out of whatever the user pasted: a full link, just
 * the fragment, or the bare payload. `null` if it isn't one.
 */
export function extractSharePayload(input: string): string | null {
  const text = input.trim();
  const hashIndex = text.indexOf('#');
  if (hashIndex !== -1) {
    const route = parseHash(text.slice(hashIndex));
    return route.kind === 'share' ? route.payload : null;
  }
  return PAYLOAD.test(text) ? text : null;
}
